import React from 'react';

import { gql, useMutation } from '@apollo/client';
import { Alert, Checkbox, FormControlLabel, Stack, Typography } from '@mui/material';
import { filter, find, get, includes, indexOf, isNil, keyBy, map, omit, pick, sortBy } from 'lodash';
import { LoadingButton } from '../../../components';
import { createScopedI18n } from '../../../i18n/i18n';
import { joinPairs, labelForEnum } from '../../../utils/libs';
import ChainRestaurantSelector from './chain_restaurant_selector';

const PERMISSION_OPTIONS = ['job_create', 'accept_application', 'manage_hiring'];
const FILTERED_PERMISSIONS = ['restaurant_root', 'restaurant_user'];

const usersDetailPageI18n = createScopedI18n('pages.users_detail');

const setRestaurantPermissionsGql = gql`
  mutation setRestaurantPermissions($userId: ID!, $restaurantId: ID!, $permissions: [PermissionInput!]!) {
    setRestaurantPermissions(userId: $userId, restaurantId: $restaurantId, permissions: $permissions) {
      success
      errors
      data {
        id
        restaurants {
          id
          permissions(userId: $userId) {
            id
            name
          }
        }
      }
    }
  }
`;

const kickUserFromRestaurantGql = gql`
  mutation kickUserFromRestaurant($userId: ID!, $restaurantId: ID!) {
    kickUserFromRestaurant(userId: $userId, restaurantId: $restaurantId) {
      success
      errors
      data {
        id
        restaurants {
          id
        }
      }
    }
  }
`;

export type Permission = {
  id?: string;
  name?: string | null;
};

type PermissionInternal = { allow: boolean } & Permission;

export type Restaurant = {
  id: string;
  name?: string | null;
  branchName?: string | null;
  permissions?: Permission[] | null;
};

export type PermissionPanelProps = {
  userId?: string | null;
  restaurant?: Restaurant | null;
  permissions?: Permission[] | null;
  disabled?: boolean;
  editable?: boolean;
  onChange?: (permissions: Permission[]) => void;
  onRestaurantChange?: (restaurant: Restaurant) => void;
  excludeRestaurantIds?: string[] | null;
  hideRestaurantSelector?: boolean;
  hideRemoveButton?: boolean;
  addRestaurantUser?: boolean;
  resetOnSuccess?: boolean;
  containerProps?: React.ComponentProps<typeof Stack>;
};

const filterPermissions = (permissions: Permission[]): Permission[] =>
  filter(permissions, (permission) => !includes(FILTERED_PERMISSIONS, permission.name));

const preparePermissions = (permissions: Permission[]): PermissionInternal[] => {
  const existedPermissions: PermissionInternal[] = map(permissions, (permission) => ({
    ...permission,
    allow: true,
  }));

  const permissionNames = keyBy(existedPermissions, 'name');
  const defaultPermissions: PermissionInternal[] = map(
    filter(PERMISSION_OPTIONS, (name) => !permissionNames[name]),
    (name) => ({ name, allow: false }),
  );

  return sortBy([...existedPermissions, ...defaultPermissions], (permission) => {
    const index = indexOf(PERMISSION_OPTIONS, permission.name);
    return index === -1 ? Infinity : index;
  });
};

const PermissionPanel = ({
  userId,
  restaurant,
  permissions,
  disabled,
  editable = true,
  onChange,
  onRestaurantChange,
  excludeRestaurantIds,
  hideRestaurantSelector,
  hideRemoveButton,
  addRestaurantUser,
  resetOnSuccess,
  containerProps,
}: PermissionPanelProps) => {
  const [restaurantInternal, setRestaurant] = React.useState<Restaurant | null>(restaurant ?? null);
  const [permissionsInternal, setPermissions] = React.useState<PermissionInternal[]>(
    preparePermissions(permissions ?? []),
  );
  const [hasChanges, setHasChanges] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState<string | null>(null);

  React.useEffect(() => {
    setRestaurant(restaurant ?? null);
  }, [restaurant]);

  React.useEffect(() => {
    setPermissions(preparePermissions(permissions ?? []));
  }, [permissions]);

  const hasPermission = React.useCallback(
    (permissionName: string) => !!find(permissionsInternal, { name: permissionName })?.allow,
    [permissionsInternal],
  );

  const restaurantChangeHandler = React.useCallback(
    (newRestaurant: Restaurant) => {
      setRestaurant(newRestaurant);
      setHasChanges(true);
      onRestaurantChange?.(newRestaurant);
    },
    [onRestaurantChange],
  );

  const togglePermission = React.useCallback(
    (permissionName: string) => {
      if (!editable) {
        return;
      }

      const newPermissions: PermissionInternal[] = map(permissionsInternal, (permission) =>
        permission.name === permissionName ? { ...permission, allow: !hasPermission(permissionName) } : permission,
      );

      setPermissions(newPermissions);
      setHasChanges(true);
      if (onChange) {
        onChange(map(filter(newPermissions, 'allow'), (p) => omit(p, 'allow')));
      }
    },
    [permissionsInternal, editable, hasPermission, onChange],
  );

  const resetChanges = React.useCallback(() => {
    setRestaurant(restaurant ?? null);
    setPermissions(preparePermissions(permissions ?? []));
    setHasChanges(false);
  }, [restaurant, permissions]);

  const [setRestaurantPermissionsMutation, { loading: setRestaurantPermissionsLoading }] =
    useMutation(setRestaurantPermissionsGql);
  const [kickUserFromRestaurantMutation, { loading: kickUserFromRestaurantLoading }] =
    useMutation(kickUserFromRestaurantGql);

  const submitPermissionsHandler = React.useCallback(async () => {
    if (isNil(userId) || isNil(restaurantInternal?.id)) {
      return;
    }

    setErrorMessage(null);
    const permissionsToSubmit = map(
      addRestaurantUser ? [...permissionsInternal, { name: 'restaurant_user', allow: true }] : permissionsInternal,
      (permission) => pick(permission, ['name', 'allow']),
    );

    try {
      const result = await setRestaurantPermissionsMutation({
        variables: {
          userId,
          restaurantId: restaurantInternal?.id,
          permissions: permissionsToSubmit,
        },
      });

      const success = get(result, 'data.setRestaurantPermissions.success', false);
      if (success) {
        setHasChanges(false);

        if (resetOnSuccess) {
          resetChanges();
        }
      } else {
        const errors = get(result, 'data.setRestaurantPermissions.errors', []);
        setErrorMessage(joinPairs(errors));
      }
    } catch (error) {
      setErrorMessage(get(error, 'message', 'Unknown error'));
    }
  }, [
    userId,
    restaurantInternal?.id,
    permissionsInternal,
    setRestaurantPermissionsMutation,
    addRestaurantUser,
    resetOnSuccess,
    resetChanges,
  ]);

  const kickUserFromRestaurantHandler = React.useCallback(async () => {
    if (isNil(userId) || isNil(restaurantInternal?.id)) {
      return;
    }

    setErrorMessage(null);

    try {
      const result = await kickUserFromRestaurantMutation({
        variables: {
          userId,
          restaurantId: restaurantInternal?.id,
        },
      });

      const success = get(result, 'data.kickUserFromRestaurant.success', false);
      if (success) {
        setHasChanges(false);

        if (resetOnSuccess) {
          resetChanges();
        }
      } else {
        const errors = get(result, 'data.kickUserFromRestaurant.errors', []);
        setErrorMessage(joinPairs(errors));
      }
    } catch (error) {
      setErrorMessage(get(error, 'message', 'Unknown error'));
    }
  }, [userId, restaurantInternal?.id, kickUserFromRestaurantMutation, resetOnSuccess, resetChanges]);

  // Buttons disabled and loading state
  const deleteButtonLoading = kickUserFromRestaurantLoading || setRestaurantPermissionsLoading;
  const deleteButtonDisabled = disabled || !userId || !restaurantInternal?.id || deleteButtonLoading;

  const confirmButtonLoading = setRestaurantPermissionsLoading || kickUserFromRestaurantLoading;
  const confirmButtonDisabled = disabled || !hasChanges || !userId || !restaurantInternal?.id || confirmButtonLoading;

  return (
    <Stack
      gap={1.5}
      {...containerProps}
      sx={[{}, ...(Array.isArray(containerProps?.sx) ? containerProps?.sx ?? [] : [containerProps?.sx])]}
    >
      {hideRestaurantSelector ? null : (
        <ChainRestaurantSelector
          disabled={disabled}
          restaurant={restaurantInternal}
          onChange={restaurantChangeHandler}
          excludeRestaurantIds={excludeRestaurantIds}
          inputProps={{ readOnly: !!restaurant }} // If restaurant prop provided, Don't allow user to change restaurant
        />
      )}

      <Stack>
        <Typography variant="body2">{usersDetailPageI18n('permission')}</Typography>
        <Stack flexDirection="row" flexWrap="wrap" paddingLeft={1} gap={1}>
          {map(filterPermissions(permissionsInternal), (permission) => (
            <FormControlLabel
              key={permission.name}
              control={
                <Checkbox
                  disabled={disabled}
                  checked={hasPermission(permission.name ?? '')}
                  onChange={() => togglePermission(permission.name ?? '')}
                />
              }
              label={labelForEnum('permissions', permission.name, { defaultValue: permission.name })}
            />
          ))}
        </Stack>
      </Stack>

      <Stack gap={1}>
        {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}
        {editable ? (
          <Stack flexDirection="row" gap={1}>
            {hideRemoveButton ? null : (
              <LoadingButton
                variant="contained"
                color="error"
                disableElevation
                loading={deleteButtonLoading}
                disabled={deleteButtonDisabled}
                onClick={kickUserFromRestaurantHandler}
              >
                {usersDetailPageI18n('delete')}
              </LoadingButton>
            )}
            <div style={{ flex: 1 }} />
            <LoadingButton
              variant="contained"
              color="primary"
              disableElevation
              loading={confirmButtonLoading}
              disabled={confirmButtonDisabled}
              onClick={submitPermissionsHandler}
            >
              {usersDetailPageI18n('confirm')}
            </LoadingButton>
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  );
};

export default PermissionPanel;
